import { isNil } from "lodash";
import createCachedSelector from "re-reselect";

import { isDateValid } from "../../../modules/definition/calendar/dateFunctions";
import { itemSelectors } from "../../../modules/items";
import { immutableRecords } from "../../../types";
import { getLayoutConstants } from "./getLayoutConstants";

/**
 *
 * @param {*} viewId                [0]
 * @param {*} ev                    [1]
 * @param {*} unitScale             [2]
 * @param {*} fontSize              [3]
 * @param {*} dateForNow            [4]
 * @param {*} hasOngoingDescendants [5]
 * @param {*} showItemCard          [6]
 * @returns
 */

export const calcEventInternalLayout = createCachedSelector(
  // inputs
  // (...params) => params[1],
  (viewId, ev) => ev.itemCardSize,
  (viewId, ev) => itemSelectors.getEventStartTs(ev),
  (viewId, ev) => ev.preferredStartDate,
  (viewId, ev) => ev.latestStartDate,
  (viewId, ev) => ev.earliestEndDate,
  (viewId, ev) => ev.endDate,
  (viewId, ev) => ev.drawPreferredEndDate,
  (viewId, ev) => ev.ongoing,
  (viewId, ev) => ev.children && ev.children.size > 0,
  (...params) => params[2],
  (...params) => params[3],
  (...params) => params[4].timestamp,
  (...params) => params[5],
  (...params) => params[6],
  // output
  (
    itemCardSize,
    start,
    preferredStartDate,
    latestStartDate,
    earliestEndDate,
    endDate,
    drawPreferredEndDate,
    ongoing,
    hasChildren,
    unitScale,
    fontSize,
    dateForNowTs,
    hasOngoingDescendants,
    showItemCard
  ) => {
    const layoutConstants = getLayoutConstants(fontSize, showItemCard);

    const cardSize = showItemCard ? itemCardSize : { width: 0, height: 0 };
    let end = isDateValid(endDate)
      ? ongoing && endDate.timestamp < dateForNowTs
        ? dateForNowTs
        : endDate.timestamp
      : ongoing
        ? dateForNowTs
        : start;
    if (hasOngoingDescendants && end < dateForNowTs) {
      end = dateForNowTs;
    }
    const preferredStart = isDateValid(preferredStartDate) ? preferredStartDate.timestamp : start;

    const preferredEnd = ongoing
      ? isDateValid(latestStartDate) && latestStartDate.timestamp > dateForNowTs
        ? latestStartDate.timestamp
        : dateForNowTs >= preferredStart
          ? dateForNowTs
          : preferredStart
      : drawPreferredEndDate.timestamp;

    let isMarkerDiamond = true;
    // What is the exact left pixel for the marker
    let markerAbsLeft = !isNil(preferredStart) ? Math.round(preferredStart / unitScale) : 0;
    let markerAbsRight = !isNil(preferredEnd) ? Math.round(preferredEnd / unitScale) : markerAbsLeft;
    // What is the exact intended width of the marker?
    let markerWidth = markerAbsRight - markerAbsLeft; //!isNil(preferredStart) ? Math.round((preferredEnd - preferredStart) / unitScale) : 0;
    // Is card shown to the left or below
    let markerLeft = layoutConstants.EVENT_RECT_PADDING;
    // Instantiate rects for uncertain and actual dates
    let uncertainStartRect = immutableRecords.FlatRectRecord();
    let uncertainEndRect = immutableRecords.FlatRectRecord();
    let startRectWithChildren = immutableRecords.FlatRectRecord();
    let endRectWithChildren = immutableRecords.FlatRectRecord();
    let parentShadowMarkerRect = immutableRecords.FlatRectRecord();

    // Uncertain dates - only show if marker is not a diamond
    let uncertainStartMarkerWidth = 0;
    let uncertainEndMarkerWidth = 0;
    let preferredWidth = markerWidth + uncertainStartMarkerWidth + uncertainEndMarkerWidth;
    if (latestStartDate && !isNil(preferredStart)) {
      const latestStart = latestStartDate.timestamp < preferredEnd ? latestStartDate.timestamp : preferredEnd;
      uncertainStartMarkerWidth = Math.ceil((latestStart - preferredStart) / unitScale);
    }
    if (earliestEndDate && !isNil(preferredEnd) && !ongoing) {
      const earliestEnd = earliestEndDate.timestamp < preferredEnd ? earliestEndDate.timestamp : preferredEnd;
      uncertainEndMarkerWidth = Math.ceil((preferredEnd - earliestEnd) / unitScale);
    }

    if (preferredWidth >= layoutConstants.MARKER_HEIGHT) {
      isMarkerDiamond = false;

      // Uncertain Start
      if (latestStartDate && !isNil(preferredStart)) {
        markerWidth = markerWidth - uncertainStartMarkerWidth;
        markerLeft = uncertainStartMarkerWidth + layoutConstants.EVENT_RECT_PADDING;

        uncertainStartRect = immutableRecords.FlatRectRecord({
          width: uncertainStartMarkerWidth,
          height: layoutConstants.MARKER_HEIGHT,
          left: layoutConstants.EVENT_RECT_PADDING,
          top: layoutConstants.EVENT_RECT_PADDING,
          right: layoutConstants.EVENT_RECT_PADDING + markerWidth,
          bottom: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT
        });
      }

      // Uncertain End
      if (earliestEndDate && !isNil(preferredEnd) && !ongoing) {
        markerWidth = markerWidth - uncertainEndMarkerWidth;

        uncertainEndRect = immutableRecords.FlatRectRecord({
          width: uncertainEndMarkerWidth,
          height: layoutConstants.MARKER_HEIGHT,
          left: markerLeft + markerWidth,
          top: layoutConstants.EVENT_RECT_PADDING,
          right: layoutConstants.EVENT_RECT_PADDING + uncertainEndMarkerWidth,
          bottom: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT
        });
      }
    }

    // Minimum width for marker
    if (isMarkerDiamond) {
      markerWidth = layoutConstants.MARKER_HEIGHT;
    }

    // Preferred Start
    if (!isNil(preferredStart) && start != preferredStart) {
      let preferredStartMarkerWidth = Math.ceil((preferredStart - start) / unitScale);
      let preferredStartMarkerLeft = layoutConstants.EVENT_RECT_PADDING;

      if (preferredStartMarkerWidth >= layoutConstants.MARKER_HEIGHT) {
        startRectWithChildren = immutableRecords.FlatRectRecord({
          width:
            preferredStartMarkerWidth -
            layoutConstants.MARKER_RIGHT_MARGIN -
            (isMarkerDiamond ? layoutConstants.MARKER_HEIGHT / 2 : 0),
          height: layoutConstants.MARKER_HEIGHT / 3,
          left: preferredStartMarkerLeft + (isMarkerDiamond ? layoutConstants.MARKER_HEIGHT / 2 : 0),
          top: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT / 3,
          right: layoutConstants.EVENT_RECT_PADDING + preferredStartMarkerWidth,
          bottom: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT
        });

        markerLeft = markerLeft + preferredStartMarkerWidth;
        markerAbsLeft = Math.round(start / unitScale);
        uncertainStartRect = uncertainStartRect
          .set("left", uncertainStartRect.left + preferredStartMarkerWidth)
          .set("right", uncertainStartRect.right + preferredStartMarkerWidth);
        uncertainEndRect = uncertainEndRect
          .set("left", uncertainEndRect.left + preferredStartMarkerWidth)
          .set("right", uncertainEndRect.right + preferredStartMarkerWidth);
      }
    }

    // Preferred End
    let preferredEndMarkerWidth;
    if (!isNil(preferredEnd) && end != preferredEnd) {
      preferredEndMarkerWidth =
        Math.ceil((end - preferredEnd) / unitScale) - (ongoing ? layoutConstants.MARKER_HEIGHT / 2 : 0);
      let preferredEndMarkerLeft =
        markerLeft +
        markerWidth +
        uncertainEndRect.width +
        layoutConstants.MARKER_RIGHT_MARGIN +
        (ongoing ? layoutConstants.MARKER_HEIGHT / 2 : 0);
      // If marker is left adjusted, shift by the adjustment
      if (isMarkerDiamond) {
        preferredEndMarkerWidth = preferredEndMarkerWidth - layoutConstants.MARKER_HEIGHT / 2;
        preferredEndMarkerLeft = preferredEndMarkerLeft - 2;
      }

      if (preferredEndMarkerWidth >= layoutConstants.MARKER_HEIGHT) {
        endRectWithChildren = immutableRecords.FlatRectRecord({
          width: preferredEndMarkerWidth - layoutConstants.MARKER_RIGHT_MARGIN,
          height: layoutConstants.MARKER_HEIGHT / 3,
          left: preferredEndMarkerLeft,
          top: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT / 3,
          right: preferredEndMarkerLeft + preferredEndMarkerWidth,
          bottom: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT
        });
      }
    }

    // Marker is always positioned at {layoutConstants.EVENT_RECT_PADDING, layoutConstants.EVENT_RECT_PADDING} within the rect
    // It is the rect position that changes.
    const markerRelativeRect = immutableRecords.FlatRectRecord({
      width: markerWidth,
      height: layoutConstants.MARKER_HEIGHT,
      left: markerLeft,
      top: layoutConstants.EVENT_RECT_PADDING,
      // When the duration is zero
      // we will place a 45 degree rotated square
      // instead of the normal rectangle.
      // So we need to leave the space here to make the right right.
      // See ATR-419
      right: markerLeft + markerWidth,
      bottom: layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT
    });

    const allMarkerWidths =
      markerRelativeRect.width +
      uncertainStartRect.width +
      uncertainEndRect.width +
      (startRectWithChildren.width ? startRectWithChildren.width + layoutConstants.MARKER_RIGHT_MARGIN : 0) +
      (endRectWithChildren.width
        ? endRectWithChildren.width +
          layoutConstants.MARKER_RIGHT_MARGIN +
          (ongoing ? layoutConstants.MARKER_HEIGHT / 2 : 0)
        : 0) +
      (ongoing ? layoutConstants.MARKER_HEIGHT / 2 : 0);
    const cardIsOnNewLine = showItemCard && allMarkerWidths > layoutConstants.MARKER_NEW_LINE_WIDTH;
    const cardLeft = showItemCard
      ? cardIsOnNewLine
        ? layoutConstants.EVENT_RECT_PADDING + (isMarkerDiamond ? layoutConstants.MARKER_HEIGHT / 2 : 0)
        : allMarkerWidths + layoutConstants.MARKER_RIGHT_MARGIN + layoutConstants.EVENT_RECT_PADDING
      : 0;
    const cardTop = showItemCard
      ? cardIsOnNewLine
        ? layoutConstants.EVENT_RECT_PADDING + markerRelativeRect.bottom + layoutConstants.MARKER_BOTTOM_MARGIN
        : layoutConstants.EVENT_RECT_PADDING_TOP_TEXT
      : 0;

    if (hasChildren && !showItemCard) {
      const parentMarkerLeft = isMarkerDiamond
        ? markerRelativeRect.left
        : (isNil(preferredStart) ? markerRelativeRect.left : uncertainStartRect.left) +
          layoutConstants.PARENT_MARKER_OFFSET;
      const parentMarkerWidth = isMarkerDiamond
        ? markerRelativeRect.width
        : markerRelativeRect.width +
          uncertainStartRect.width +
          uncertainEndRect.width -
          layoutConstants.PARENT_MARKER_OFFSET;

      const parentMarkerTop = markerRelativeRect.top + layoutConstants.PARENT_MARKER_OFFSET;
      parentShadowMarkerRect = immutableRecords.FlatRectRecord({
        left: parentMarkerLeft,
        width: parentMarkerWidth,
        right: parentMarkerLeft + parentMarkerWidth,
        top: parentMarkerTop,
        height: markerRelativeRect.height,
        bottom: parentMarkerTop + markerRelativeRect.height
      });
    }

    const eventCardRelativeRect = immutableRecords.FlatRectRecord({
      width: cardSize.width,
      height: cardSize.height,
      left: cardLeft,
      top: cardTop,
      right: cardLeft + cardSize.width,
      bottom: cardTop + cardSize.height
    });

    const childrenArrowLeft = hasChildren
      ? Math.max(allMarkerWidths, eventCardRelativeRect.right) +
        layoutConstants.CHILDREN_ARROW_LEFT_MARGIN +
        (isMarkerDiamond && showItemCard ? layoutConstants.MARKER_HEIGHT / 2 : 0) +
        (!preferredEndMarkerWidth && ((cardIsOnNewLine && showItemCard) || !showItemCard)
          ? layoutConstants.MARKER_HEIGHT
          : 0)
      : 0;
    const childrenArrowTop = hasChildren ? layoutConstants.CHILDREN_ARROW_ONE_LINE_TOP : 0;
    const childrenArrowRect = immutableRecords.FlatRectRecord({
      width: hasChildren ? layoutConstants.CHILDREN_ARROW_SIZE : 0,
      height: hasChildren ? layoutConstants.CHILDREN_ARROW_SIZE : 0,
      left: childrenArrowLeft,
      top: childrenArrowTop,
      right: childrenArrowLeft + (hasChildren ? layoutConstants.CHILDREN_ARROW_SIZE : 0),
      bottom: childrenArrowTop + (hasChildren ? layoutConstants.CHILDREN_ARROW_SIZE : 0)
    });

    // How far left of true position do we need to move total rect to line up the event correctly
    // The marker as a child in the node parent will always have one pixel difference
    // because of the border width of the node is 1 pixel
    // Therefore same value of the calculated left for the child and the parent
    // will have 1 pixel difference
    const leftAdjustment = isMarkerDiamond
      ? -(layoutConstants.EVENT_RECT_PADDING + layoutConstants.MARKER_HEIGHT / 2 - 1)
      : -(layoutConstants.EVENT_RECT_PADDING + 1);
    const totalLeft = markerAbsLeft + leftAdjustment;

    const xPadding = showItemCard
      ? cardIsOnNewLine && !hasChildren
        ? layoutConstants.EVENT_RECT_PADDING * 2
        : layoutConstants.EVENT_RECT_PADDING
      : layoutConstants.EVENT_RECT_PADDING * 2;

    const childrenArrowAffectOnWidth = showItemCard ? childrenArrowRect.right : allMarkerWidths;

    const totalWidth = Math.max(allMarkerWidths, eventCardRelativeRect.right, childrenArrowAffectOnWidth) + xPadding;

    const totalHeight =
      Math.max(
        markerRelativeRect.bottom,
        eventCardRelativeRect.bottom,
        childrenArrowRect.bottom,
        parentShadowMarkerRect.bottom
      ) + layoutConstants.EVENT_RECT_PADDING;

    const totalRect = immutableRecords.FlatRectRecord({
      left: totalLeft,
      top: 0,
      width: totalWidth,
      height: totalHeight,
      right: totalLeft + totalWidth,
      bottom: totalHeight
    });

    return immutableRecords.TimelineViewEventPositionRecord({
      markerRelativeRect,
      eventCardRelativeRect,
      totalRect,
      childrenArrowRect,
      parentShadowMarkerRect,
      uncertainStartRect,
      uncertainEndRect,
      startRectWithChildren,
      endRectWithChildren,
      cardIsOnNewLine
    });
  }
)((viewId, ev) => `${viewId}-${ev.id}`);
